import dayjs from 'dayjs';
import React from 'react';

import { SimpleImage } from '$partials/media';
import { IconPlay } from '$partials/icons';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const ArticleInformation = ({
    authors = null,
    orientation = '',
    publicationDate = ''
}) => {
    const formatter = new Intl.ListFormat(
        'en',
        {
            type: 'conjunction',
            style: 'long'
        }
    );

    return (
        <div className={orientation === 'horizontal' ? 'flex-shrink-0 md:w-2/8 md:pl-32' : ''}>
            <div className={`${orientation === 'horizontal' ? 'grid md:justify-between md:grid-rows-2 grid-cols-2 md:grid-cols-1 border-t md:border-t-0 md:border-l border-border-default w-full h-full' : 'flex'}`}>
                {authors?.length > 0 && (
                    <div className={`${orientation === 'horizontal' ? 'h-full md:w-full md:border-r-0 md:border-b' : 'w-1/2'} border-r border-border-default flex flex-col px-8 md:px-16`}>
                        <p className='type-style-single-line-eyebrow pt-16'>
                            {'Author'}
                        </p>

                        <p className='pt-8 pb-16 type-style-listing-body'>
                            {formatter.format(authors?.map(el => el.title))}
                        </p>
                    </div>
                )}

                <div className='flex flex-col px-16'>
                    <p className='type-style-single-line-eyebrow pt-16'>
                        {'Date'}
                    </p>

                    <p className='pt-8 pb-16 type-style-listing-body'>
                        {dayjs(publicationDate).format('DD MMMM YYYY')}
                    </p>
                </div>
            </div>
        </div>
    );
};




const ArticleStandfirst = ({
    ctaLabel,
    articleStandfirst = '',
    hasImage = false,
    listingText = '',
    orientation = '',
    parent = null,
    title = '',
    url = ''
}) => {
    return (
        <div className={`${orientation === 'horizontal' ? 'flex-grow pl-8 md:pl-16 md:pr-[120px]' : 'flex-1 md:pr-16 border-b border-border-default px-8 md:px-16'} pr-48 flex flex-col py-8 md:py-16`}>
            {/* Type */}
            {!hasImage && (
                <span className='tag-primary self-start mb-16'>
                    {parent?.title}
                </span>
            )}

            <p className='type-style-sans-h3-med'>
                {title}
            </p>

            {(listingText || articleStandfirst) && (
                <p className='mt-8 type-style-serif-body'>
                    {listingText || articleStandfirst}
                </p>
            )}

            <a
                className='expand-interaction__action hover:underline hover:underline-offset-2 mt-20'
                href={url}>
                {ctaLabel}
            </a>
        </div>
    );
};




const ArticleHorizontal = ({
    showPlayButton,
    image = null,
    parent = null,
    ...props
}) => {
    return (
        <>
            {image && (
                <div className='flex flex-col gap-y-16 px-8 md:px-16 pt-8 md:pt-16'>
                    {/* Type */}
                    <span className='tag-primary self-start'>
                        {parent?.title}
                    </span>

                    {image && (
                        <div className={'h-[185px] sm:h-[230px] md:h-[495px] w-full rounded-lg overflow-hidden relative flex'}>
                            <div className='bg-[--tint-dark-10] w-full h-full absolute top-0 left-0 hidden card-detail--tint'/>

                            {showPlayButton && (
                                <div className='absolute right-8 bottom-8'>
                                    <span
                                        className='p-12 inline-flex items-center bg-button-tint-surface rounded-full'>
                                        <IconPlay/>
                                    </span>
                                </div>
                            )}

                            <SimpleImage
                                {...image}
                                className='w-full h-full object-cover transition-transform duration-300 pointer-events-none select-none' />
                        </div>
                    )}
                </div>
            )}

            <div className={'md:flex-row flex-col flex group'}>
                {props?.children}
            </div>
        </>
    );
};




/**
 * @type {import('$types/components/partials/items.d.ts').ArticleListingItem}
 */
const ArticleListingItem = ({
    __typename = '',
    articleStandfirst = '',
    authors = '',
    heroMedia = null,
    listingImage = null,
    listingText = '',
    orientation = '',
    parent = null,
    publicationDate = '',
    title = '',
    url = ''
}) => {
    const image = listingImage || heroMedia?.image || null;

    const showPlayButton = __typename !== 'ArticleDetailPage' || parent.title?.toLowerCase() === 'podcasts' || parent.title?.toLowerCase() === 'video';

    let ctaLabel;
    switch (__typename) {
        case 'PodcastDetailPage':
            ctaLabel = 'Listen Here';

        case 'VideoDetailPage':
            ctaLabel = 'Watch Here';

        case 'ArticleDetailPage':
            if (parent.title?.toLowerCase() === 'podcasts') {
                ctaLabel = 'Listen Here';
            } else if (parent.title?.toLowerCase() === 'video') {
                ctaLabel = 'Watch Here';
            }

            ctaLabel = 'Read Article';

        default:
            ctaLabel = 'Read Article';
    }

    return (
        <div className='flex-grow w-full h-full rounded-listing-item bg-surface-raised expand-interaction hover:-translate-y-4 ease-linear duration-[80ms] transition-all card-detail'>
            {/* Wide with Listing Image */}
            {orientation === 'wide' && image && (
                <div className={'hidden sm:grid grid-cols-2-1 -mx-16 md:-mx-8'}>
                    <div className='px-8'>
                        <div className='pl-16 py-8 md:py-16'>
                            <div className='h-[353px] md:h-[505px] w-full rounded-lg overflow-hidden relative flex'>
                                <div className='bg-[--tint-dark-10] w-full h-full absolute top-0 left-0 hidden card-detail--tint' />

                                {showPlayButton && (
                                    <div className='absolute right-8 bottom-8'>
                                        <span
                                            className='p-12 inline-flex items-center bg-button-tint-surface rounded-full'>
                                            <IconPlay/>
                                        </span>
                                    </div>
                                )}

                                <SimpleImage
                                    {...image}
                                    className='w-full h-full object-cover transition-transform duration-300 pointer-events-none select-none'/>
                            </div>
                        </div>
                    </div>

                    <div className='-ml-8 pr-8 pt-16 flex h-full flex-col'>
                        <div className='flex flex-col flex-grow gap-y-16 px-8 md:px-16 pb-16'>
                            {/* Type */}
                            <span className='tag-primary self-start'>
                                {parent?.title}
                            </span>

                            <p className='type-style-sans-h3-med'>
                                {title}
                            </p>

                            {(listingText || articleStandfirst) && (
                                <p className='type-style-serif-body'>
                                    {listingText || articleStandfirst}
                                </p>
                            )}

                            <a
                                className='expand-interaction__action hover:underline hover:underline-offset-2'
                                href={url}>
                                {ctaLabel}
                            </a>
                        </div>

                        <div className='sm:pr-8 md:pr-0'>
                            <div className='border-t border-border-default'>
                                <ArticleInformation
                                    authors={authors}
                                    orientation={orientation}
                                    publicationDate={publicationDate}/>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Wide without ListingImage uses Horizontal layout */}
            {orientation === 'wide' && !image && (
                <ArticleHorizontal
                    image={image}
                    parent={parent}
                    showPlayButton={showPlayButton}
                    url={url}>
                    <ArticleStandfirst
                        articleStandfirst={articleStandfirst}
                        ctaLabel={ctaLabel}
                        hasImage={Boolean(image)}
                        listingText={listingText}
                        orientation='horizontal'
                        parent={parent}
                        title={title}
                        url={url} />

                    <ArticleInformation
                        authors={authors}
                        orientation='horizontal'
                        publicationDate={publicationDate}/>
                </ArticleHorizontal>
            )}

            {orientation === 'horizontal' && (
                <ArticleHorizontal
                    image={image}
                    parent={parent}
                    showPlayButton={showPlayButton}
                    url={url}>
                    <ArticleStandfirst
                        articleStandfirst={articleStandfirst}
                        ctaLabel={ctaLabel}
                        hasImage={Boolean(image)}
                        listingText={listingText}
                        orientation='horizontal'
                        parent={parent}
                        title={title}
                        url={url} />

                    <ArticleInformation
                        authors={authors}
                        orientation='horizontal'
                        publicationDate={publicationDate}/>
                </ArticleHorizontal>
            )}

            {(orientation !== 'horizontal' && orientation !== 'wide') && (
                <div className='h-full flex flex-col'>
                    {image && (
                        <div className='flex flex-col gap-y-16 px-8 md:px-16 pt-8 md:pt-16'>
                            {/* Type */}
                            <span className='tag-primary self-start'>
                                {parent?.title}
                            </span>

                            {image && (
                                <div className={'h-[185px] sm:h-[230px] w-full rounded-lg overflow-hidden relative flex'}>
                                    <div className='bg-[--tint-dark-10] w-full h-full absolute top-0 left-0 hidden card-detail--tint' />

                                    {showPlayButton && (
                                        <div className='absolute right-8 bottom-8'>
                                            <span
                                                className='p-12 inline-flex items-center bg-button-tint-surface rounded-full'>
                                                <IconPlay/>
                                            </span>
                                        </div>
                                    )}

                                    <SimpleImage
                                        {...image}
                                        className='w-full h-full object-cover transition-transform duration-300 pointer-events-none select-none'/>
                                </div>
                            )}
                        </div>
                    )}

                    <div className='flex flex-col flex-1 group'>
                        <ArticleStandfirst
                            articleStandfirst={articleStandfirst}
                            ctaLabel={ctaLabel}
                            hasImage={Boolean(image)}
                            listingText={listingText}
                            orientation={orientation}
                            parent={parent}
                            title={title}
                            url={url} />

                        <ArticleInformation
                            authors={authors}
                            orientation={orientation}
                            publicationDate={publicationDate}/>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default ArticleListingItem;
